import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react"
import { createPortal } from "react-dom"
import * as Dialog from "@radix-ui/react-dialog"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { AudioLines, CircleHelp, Loader2, Repeat, Scissors, Trash2, X } from "lucide-react"
import {
  PlaybackTransport,
  PlayerIconButton,
  usePlaybackTransport,
} from "@/components/timeline-player/playback-transport"
import { PlayerPipButton, PlayerPipOverlay, PlayerPipProvider } from "@/components/timeline-player/player-pip"
import { StartPlayOverlay } from "@/components/timeline-player/start-play-overlay"
import { TimecodeDisplay } from "@/components/timeline-player/timeline-chrome"
import { TimelineLanes, type TimelineLanesMetrics } from "@/components/timeline-player/timeline-lanes"
import { VideoShellOverlay } from "@/components/timeline-player/video-shell-overlay"
import { Button, Kbd } from "@/components/ui/primitives"
import { usePointerIdle } from "@/hooks/use-pointer-idle"
import { useShellFullscreen } from "@/hooks/use-shell-fullscreen"
import { useTranslation } from "@/hooks/use-translation"
import type { PipFrame } from "@/lib/player-pip"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"
import {
  bladeAt,
  clipDuration,
  clipVersionId,
  collectSnapPoints,
  moveClip,
  readPlayerAudioScrubPref,
  readPlayerLoopPref,
  removeClip,
  resizeClip,
  snapEdge,
  snapMoveStart,
  timelineDuration,
  useTimelinePlayback,
  writePlayerAudioScrubPref,
  writePlayerLoopPref,
  type EngineSources,
  type PlaybackTimeStore,
  type TimelineDocument,
  type TimelinePlaybackControls,
  type TimelineSource,
} from "@/lib/timeline"
import { cn } from "@/lib/utils"

export type TimelinePlayerCapabilities = {
  blade?: boolean
  resize?: boolean
  deleteClips?: boolean
  moveClips?: boolean
}

/** Imperative playback API for place-specific wrappers (animatic segment strip, etc.). */
export type TimelinePlayerPlaybackApi = {
  controls: TimelinePlaybackControls
  timeStore: PlaybackTimeStore
  /** Актуальный playing без React-рендеров — для авто-скролла внешнего playhead. */
  playingRef: RefObject<boolean>
  captureCurrentFrame: () => Promise<HTMLImageElement>
}

export type TimelinePlayerProps = {
  doc: TimelineDocument
  sources: EngineSources
  /** Метаданные источников для filmstrip на клипах (ключ — version_id). */
  timelineSources?: Record<string, TimelineSource>
  aspectRatio?: number | null
  frameRate?: number
  /**
   * Полная длительность исходного медиа (мс). Линейка и fit опираются на неё,
   * а не на timelineDuration(doc) — подрезка клипов не сжимает линейку.
   */
  mediaDurationMs?: number | null
  /** Когда false — пауза и без шорткатов (например, скрытая вкладка). */
  active?: boolean
  /** Один раз запустить playback при активации (например модалка превью). */
  autoPlay?: boolean
  /** Начальное значение loop; если не задано — из localStorage-префа плеера. */
  defaultLoop?: boolean
  capabilities?: TimelinePlayerCapabilities
  /**
   * true — doc это несохранённый дефолт (полный клип): по loadedmetadata его можно
   * дотянуть до реальной длительности. Для сохранённого состояния оставь false,
   * иначе подрезанный до конца клип «восстановится» при каждом открытии.
   */
  isDefaultDoc?: boolean
  /** reason="metadata" — авто-коррекция по loadedmetadata, а не правка пользователя. */
  onDocChange?: (doc: TimelineDocument, meta?: { reason: "edit" | "metadata" }) => void
  /**
   * Колбэк с controls/timeStore для внешней синхронизации (сегменты аниматика и т.п.).
   * При unmount вызывается с null.
   */
  onPlaybackReady?: (api: TimelinePlayerPlaybackApi | null) => void
  /** Натуральный размер кадра активного видеоисточника. */
  onMediaSize?: (versionId: number, size: { width: number; height: number }) => void
  /**
   * Левая колонка лейблов треков (рядом с линейкой и «Видео»).
   * Для single-track preview обычно false. Default true — как раньше.
   */
  showTrackHeaders?: boolean
  /**
   * false — только видео-shell + транспорт + timecode (без ruler/lanes/playhead).
   * Нужно, когда таймлайн вынесен наружу (например аниматик).
   */
  showTimeline?: boolean
  /** Слот поверх video-shell (например превью активной метки аниматика). */
  shellOverlay?: ReactNode
  /**
   * Интерактивный слой поверх кадра (например SketchCanvas).
   * Full shell (inset-0) so overlay letterbox matches video object-fit:contain;
   * transport controls stay clickable via higher z-index.
   */
  interactiveShellOverlay?: ReactNode
  /** Corner action above transport (e.g. Save markup) — z above controls. */
  shellFloatingAction?: ReactNode
  /**
   * Stretch to parent height and pin transport + timeline to the bottom
   * (e.g. video-correction modal aligned with the Start button).
   */
  fillHeight?: boolean
  className?: string
  /**
   * Куда вынести блок транспорта (таймкод и кнопки). Дорожки таймлайна остаются в плеере.
   * Не задан — транспорт рисуется под кадром.
   */
  transportHost?: HTMLElement | null
  /**
   * Кадр заполняет ячейку родителя: без max-height и без своего aspect-ratio.
   * Родитель уже держит формат кадра.
   */
  fitFrame?: boolean
  /** Слот сразу после кнопки цикла, до аудио-скраба. */
  afterLoop?: ReactNode
  /**
   * Картинка в картинке. null — кнопки нет.
   * Время в аргументе — время плеера; localMs уже в шкале шота.
   */
  pipResolve?: ((timeMs: number) => PipFrame | null) | null
}

type ClipSelection = { trackId: string; clipId: string }

const SNAP_PX = 8

const SHORTCUT_SECTIONS = [
  {
    titleKey: "player.shortcutsPlayback",
    rows: [
      { keys: [["Space"]], labelKey: "player.shortcutPlayPause" },
      { keys: [["J"]], labelKey: "player.shortcutReverse" },
      { keys: [["K"]], labelKey: "player.shortcutPause" },
      { keys: [["L"]], labelKey: "player.shortcutPlayForward" },
    ],
  },
  {
    titleKey: "player.shortcutsSeek",
    rows: [
      { keys: [["←"]], labelKey: "player.shortcutFrameBack" },
      { keys: [["→"]], labelKey: "player.shortcutFrameForward" },
      { keys: [["Shift", "←"]], labelKey: "player.shortcutSeekBack" },
      { keys: [["Shift", "→"]], labelKey: "player.shortcutSeekForward" },
      { keys: [["Home"]], labelKey: "player.shortcutToStart" },
      { keys: [["End"]], labelKey: "player.shortcutToEnd" },
    ],
  },
  {
    titleKey: "player.shortcutsEdit",
    rows: [
      { keys: [["B"]], labelKey: "timeline.shortcutBlade" },
      { keys: [["Delete"], ["Backspace"]], labelKey: "timeline.shortcutDeleteClip" },
    ],
  },
] as const

/**
 * Переиспользуемый плеер с таймлайном: превью, транспорт, линейка и дорожки.
 * Редактирование включается через capabilities (blade / resize / delete / move).
 * Без shot-специфики: render, lipsync, add-track, audio compose.
 */
export function TimelinePlayer({
  doc,
  sources,
  timelineSources = {},
  aspectRatio = null,
  frameRate = DEFAULT_VIDEO_OUTPUT_FPS,
  mediaDurationMs: mediaDurationMsProp = null,
  active = true,
  autoPlay = false,
  defaultLoop,
  capabilities,
  isDefaultDoc = false,
  onDocChange,
  onPlaybackReady,
  onMediaSize,
  showTrackHeaders = true,
  showTimeline = true,
  shellOverlay,
  interactiveShellOverlay,
  shellFloatingAction,
  fillHeight = false,
  className,
  transportHost = null,
  fitFrame = false,
  afterLoop,
  pipResolve = null,
}: TimelinePlayerProps) {
  const { t } = useTranslation(["shot", "common"])
  const fps = frameRate > 0 ? frameRate : DEFAULT_VIDEO_OUTPUT_FPS
  const frameMs = 1000 / fps
  const shellAspectRatio = aspectRatio != null && aspectRatio > 0 ? aspectRatio : 16 / 9
  const lanesMetricsRef = useRef<TimelineLanesMetrics>({ pxPerMs: 0 })

  const canBlade = capabilities?.blade === true
  const canResize = capabilities?.resize === true
  const canDelete = capabilities?.deleteClips === true
  const canMove = capabilities?.moveClips === true

  const rootRef = useRef<HTMLDivElement>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const docRef = useRef(doc)
  docRef.current = doc
  const versionDurationsRef = useRef(new Map<number, number>())
  const dirtyRef = useRef(false)
  const isDefaultDocRef = useRef(isDefaultDoc)
  isDefaultDocRef.current = isDefaultDoc
  const [discoveredSourceMs, setDiscoveredSourceMs] = useState(0)

  const [selected, setSelected] = useState<ClipSelection[]>([])
  const selectedRef = useRef(selected)
  selectedRef.current = selected
  const [loop, setLoop] = useState(() => defaultLoop ?? readPlayerLoopPref())
  const [audioScrub, setAudioScrub] = useState(() => readPlayerAudioScrubPref())
  const [muted, setMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)

  const sourcesKey = useMemo(() => JSON.stringify(sources.versionUrls), [sources.versionUrls])

  useEffect(() => {
    dirtyRef.current = false
    setDiscoveredSourceMs(0)
    versionDurationsRef.current.clear()
  }, [sourcesKey])

  const applyDoc = useCallback(
    (next: TimelineDocument) => {
      dirtyRef.current = true
      docRef.current = next
      onDocChange?.(next, { reason: "edit" })
    },
    [onDocChange],
  )

  const handleVersionDuration = useCallback(
    (versionId: number, durationMs: number) => {
      versionDurationsRef.current.set(versionId, durationMs)
      setDiscoveredSourceMs((prev) => Math.max(prev, durationMs))
      // Explicit trim/window length from caller — do not mutate clip in/out (e.g. video correction modal).
      if (mediaDurationMsProp != null && mediaDurationMsProp > 0) return

      const current = docRef.current
      let changed = false
      const tracks = current.tracks.map((track) => {
        if (track.kind === "custom_audio") return track
        const clips = track.clips.map((clip) => {
          if (clipVersionId(track, clip) !== versionId) return clip
          // Дотягиваем до реальной длительности только несохранённый дефолтный клип —
          // сохранённую подрезку (в т.ч. один клип от нуля) не трогаем.
          const isFullDefault =
            !dirtyRef.current && isDefaultDocRef.current && clip.source_in_ms === 0 && track.clips.length === 1
          if (isFullDefault && clip.source_out_ms !== durationMs) {
            changed = true
            return { ...clip, source_out_ms: durationMs }
          }
          if (clip.source_out_ms > durationMs) {
            changed = true
            return { ...clip, source_out_ms: Math.max(durationMs, clip.source_in_ms + 1) }
          }
          return clip
        })
        return { ...track, clips }
      })
      if (!changed) return
      const next = { ...current, tracks }
      docRef.current = next
      onDocChange?.(next, { reason: "metadata" })
    },
    [onDocChange, mediaDurationMsProp],
  )

  // Тумблер слышимого скраба показываем, только когда движку реально есть что фетчить:
  // потребители TimelinePlayer без scrubUrls/assetUrls получили бы мёртвый контрол.
  const hasScrubSources = Object.keys(sources.assetUrls).length > 0 || Object.keys(sources.scrubUrls ?? {}).length > 0

  const playback = useTimelinePlayback({
    doc,
    sources,
    onVersionDuration: handleVersionDuration,
    onVersionMediaSize: onMediaSize,
  })
  const { controls, timeStore, playing, buffering } = playback
  const playingRef = useRef(playing)
  playingRef.current = playing
  const endMsRef = useRef(1)
  const { forwardRate, pauseAll, togglePlay, stepFrames, stepMs, goToStart, goToEnd, playForward, playReverse } =
    usePlaybackTransport({
      controls,
      playing,
      frameMs,
      getEndMs: () => endMsRef.current,
      active,
    })

  const { isFullscreen, isPseudoFullscreen, toggleFullscreen } = useShellFullscreen(
    shellRef,
    controls.enterNativeVideoFullscreen,
  )
  // Автоскрытие контролов в фулскрине: только во время воспроизведения; на паузе видны.
  const {
    active: pointerActive,
    onControlsPointerEnter,
    onControlsPointerLeave,
    wasRevealTap,
  } = usePointerIdle({ containerRef: shellRef, enabled: isFullscreen && playing })
  const controlsVisible = !isFullscreen || pointerActive

  useEffect(() => {
    if (!onPlaybackReady) return
    onPlaybackReady({
      controls,
      timeStore,
      playingRef,
      captureCurrentFrame: () => controls.captureCurrentFrame(),
    })
    return () => onPlaybackReady(null)
  }, [onPlaybackReady, controls, timeStore])

  useEffect(() => {
    controls.setLoop(loop)
  }, [controls, loop])
  useEffect(() => {
    controls.setAudioScrub(audioScrub)
  }, [controls, audioScrub])
  useEffect(() => {
    controls.setMasterMuted(muted)
  }, [controls, muted])
  useEffect(() => {
    controls.setMasterVolume(volume)
  }, [controls, volume])

  const didAutoPlayRef = useRef(false)
  useEffect(() => {
    didAutoPlayRef.current = false
  }, [sourcesKey])
  useEffect(() => {
    if (!active) {
      didAutoPlayRef.current = false
      return
    }
    if (!autoPlay || didAutoPlayRef.current) return
    controls.play()
    didAutoPlayRef.current = true
  }, [active, autoPlay, controls, sourcesKey])

  const contentDurationMs = useMemo(() => timelineDuration(doc), [doc])

  const sourceDurationMs = useMemo(() => {
    // Explicit prop wins (trim window / known clip length) — do not stretch ruler
    // with discovered full-source duration when caller already set the window.
    if (mediaDurationMsProp != null && mediaDurationMsProp > 0) {
      return mediaDurationMsProp
    }
    let max = discoveredSourceMs
    for (const source of Object.values(timelineSources)) {
      if (source.duration_ms != null && source.duration_ms > 0) {
        max = Math.max(max, source.duration_ms)
      }
    }
    return max
  }, [mediaDurationMsProp, discoveredSourceMs, timelineSources])

  /** Линейка: явный mediaDurationMs, иначе max(исходник, контент). */
  const rulerDurationMs = Math.max(sourceDurationMs, contentDurationMs, 1)
  endMsRef.current = rulerDurationMs

  const handleViewportClick = useCallback(() => {
    // Фулскрин на таче: тап по скрытым контролам только показывает их, а не ставит паузу.
    if (isFullscreen && wasRevealTap()) return
    togglePlay()
  }, [isFullscreen, wasRevealTap, togglePlay])

  const handleBlade = useCallback(() => {
    if (!canBlade) return
    const at = controls.currentMs()
    const sels = selectedRef.current
    const primary = sels.length > 0 ? sels[sels.length - 1] : undefined
    applyDoc(bladeAt(docRef.current, at, primary))
  }, [canBlade, controls, applyDoc])

  const handleDeleteSelected = useCallback(() => {
    if (!canDelete) return
    const sels = selectedRef.current
    if (sels.length === 0) return
    let next = docRef.current
    for (const sel of sels) next = removeClip(next, sel.trackId, sel.clipId)
    applyDoc(next)
    setSelected([])
  }, [canDelete, applyDoc])

  const sourceDurationForClip = useCallback(
    (trackId: string, clipId: string): number | null => {
      const track = docRef.current.tracks.find((tr) => tr.id === trackId)
      if (!track || track.kind === "custom_audio") return null
      const clip = track.clips.find((c) => c.id === clipId)
      if (!clip) return null
      const versionId = clipVersionId(track, clip)
      if (versionId == null) return null
      return versionDurationsRef.current.get(versionId) ?? timelineSources[String(versionId)]?.duration_ms ?? null
    },
    [timelineSources],
  )

  const handleSelectClip = useCallback((trackId: string, clipId: string, additive: boolean) => {
    setSelected((prev) => {
      if (!additive) return [{ trackId, clipId }]
      const exists = prev.some((sel) => sel.clipId === clipId)
      if (exists) return prev.filter((sel) => sel.clipId !== clipId)
      return [...prev, { trackId, clipId }]
    })
  }, [])

  const snapToleranceMs = useCallback(() => {
    const currentPx = lanesMetricsRef.current.pxPerMs
    return currentPx > 0 ? SNAP_PX / currentPx : 0
  }, [])

  const handleMoveClip = useCallback(
    (trackId: string, clipId: string, newStartMs: number) => {
      if (!canMove) return
      let startMs = newStartMs
      const toleranceMs = snapToleranceMs()
      if (toleranceMs > 0) {
        const clip = docRef.current.tracks.find((tr) => tr.id === trackId)?.clips.find((c) => c.id === clipId)
        if (clip) {
          const points = collectSnapPoints(docRef.current, trackId, clipId, controls.currentMs())
          startMs = snapMoveStart(newStartMs, clipDuration(clip), points, toleranceMs)
        }
      }
      applyDoc(moveClip(docRef.current, trackId, clipId, startMs))
    },
    [canMove, applyDoc, snapToleranceMs, controls],
  )

  const handleResizeClip = useCallback(
    (trackId: string, clipId: string, edge: "left" | "right", newEdgeMs: number) => {
      if (!canResize) return
      const sourceDuration = sourceDurationForClip(trackId, clipId)
      let edgeMs = newEdgeMs
      const toleranceMs = snapToleranceMs()
      if (toleranceMs > 0) {
        const points = collectSnapPoints(docRef.current, trackId, clipId, controls.currentMs())
        edgeMs = snapEdge(newEdgeMs, points, toleranceMs)
      }
      applyDoc(resizeClip(docRef.current, trackId, clipId, edge, edgeMs, sourceDuration))
    },
    [canResize, applyDoc, sourceDurationForClip, snapToleranceMs, controls],
  )

  const handleClipDragEnd = useCallback(() => {}, [])

  useEffect(() => {
    if (!active) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey) return
      const target = e.target as HTMLElement | null
      if (
        target?.closest(
          'input, textarea, select, [contenteditable="true"], [contenteditable=""], [contenteditable]:not([contenteditable="false"]), .pe-prosemirror, .ProseMirror',
        ) ||
        target?.isContentEditable
      ) {
        return
      }
      const dialog = target?.closest('[role="dialog"], [role="alertdialog"]')
      if (dialog && rootRef.current && !dialog.contains(rootRef.current)) return

      switch (e.code) {
        case "Space":
          e.preventDefault()
          togglePlay()
          return
        case "ArrowLeft":
          e.preventDefault()
          if (e.shiftKey) stepMs(-1000)
          else stepFrames(-1)
          return
        case "ArrowRight":
          e.preventDefault()
          if (e.shiftKey) stepMs(1000)
          else stepFrames(1)
          return
        case "Home":
          e.preventDefault()
          goToStart()
          return
        case "End":
          e.preventDefault()
          goToEnd()
          return
        case "KeyJ":
          e.preventDefault()
          playReverse()
          return
        case "KeyK":
          e.preventDefault()
          pauseAll()
          return
        case "KeyL":
          e.preventDefault()
          playForward()
          return
        case "KeyB":
          if (!canBlade) return
          e.preventDefault()
          handleBlade()
          return
        case "Delete":
        case "Backspace":
          if (!canDelete) return
          e.preventDefault()
          handleDeleteSelected()
          return
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [
    active,
    canBlade,
    canDelete,
    togglePlay,
    stepFrames,
    stepMs,
    goToStart,
    goToEnd,
    playReverse,
    pauseAll,
    playForward,
    handleBlade,
    handleDeleteSelected,
  ])

  // Драг по линейке — scrubTo: сик коалесится до одного за кадр анимации,
  // иначе каждый pointermove ставил бы в очередь свой Range-запрос.
  const handleRulerSeek = useCallback(
    (ms: number) => {
      controls.scrubTo(ms)
    },
    [controls],
  )

  const handleRulerScrubEnd = useCallback(() => {
    controls.endScrub()
  }, [controls])

  // Фулскрин-ползунок: scrubTo требует паузы (иначе scrub-rAF и play-tick дерутся
  // за мастер-видео), но, в отличие от линейки, воспроизведение возобновляем —
  // стандартное поведение ползунка видеоплеера.
  const overlayResumeRateRef = useRef<number | null>(null)

  const handleOverlayScrubStart = useCallback(() => {
    overlayResumeRateRef.current = controls.isPlaying() ? controls.getRate() : null
    controls.pause()
  }, [controls])

  const handleOverlayScrubEnd = useCallback(() => {
    controls.endScrub()
    const rate = overlayResumeRateRef.current
    overlayResumeRateRef.current = null
    if (rate != null) controls.play(rate)
  }, [controls])

  const handleSeek = useCallback(
    (ms: number) => {
      controls.seek(ms)
    },
    [controls],
  )

  const selectedIdsByTrack = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const sel of selected) {
      const list = map.get(sel.trackId)
      if (list) list.push(sel.clipId)
      else map.set(sel.trackId, [sel.clipId])
    }
    return map
  }, [selected])

  const showEditToolbar = canBlade || canDelete
  const transportInline = !transportHost && !fitFrame

  const transport = (
    <TooltipPrimitive.Provider delayDuration={350} skipDelayDuration={0}>
      <div className="@container w-full min-w-0">
        <div className="flex w-full min-w-0 flex-col items-center gap-y-2 @[42rem]:grid @[42rem]:grid-cols-[1fr_auto_1fr] @[42rem]:items-center @[42rem]:gap-x-2 @[42rem]:gap-y-0">
          <div className="flex shrink-0 items-center justify-center gap-2 @[42rem]:justify-self-start">
            <TimecodeDisplay store={timeStore} fps={fps} durationMs={rulerDurationMs} title={t("player.timecode")} />
          </div>

          <PlaybackTransport
            className="@[42rem]:justify-self-center"
            playing={playing}
            onTogglePlay={togglePlay}
            onStepFrames={stepFrames}
            onGoToStart={goToStart}
            onGoToEnd={goToEnd}
          >
            <PlayerIconButton
              label={t("player.loop")}
              onClick={() =>
                setLoop((v) => {
                  const next = !v
                  writePlayerLoopPref(next)
                  return next
                })
              }
              icon={<Repeat className="h-4 w-4" />}
              activeState={loop}
            />
            {afterLoop}
            <PlayerPipButton />
            {hasScrubSources ? (
              <PlayerIconButton
                label={t("player.audioScrub")}
                onClick={() =>
                  setAudioScrub((v) => {
                    const next = !v
                    writePlayerAudioScrubPref(next)
                    return next
                  })
                }
                icon={<AudioLines className="h-4 w-4" />}
                activeState={audioScrub}
              />
            ) : null}
            {forwardRate > 1 && playing ? (
              <span className="ml-0.5 text-[10px] font-medium text-ui-accent-text tabular-nums">{forwardRate}x</span>
            ) : null}
          </PlaybackTransport>

          <div className="flex shrink-0 items-center justify-center gap-0.5 @[42rem]:justify-self-end">
            {canBlade ? (
              <PlayerIconButton
                label={t("timeline.blade")}
                onClick={handleBlade}
                icon={<Scissors className="h-4 w-4" />}
              />
            ) : null}
            {canDelete ? (
              <PlayerIconButton
                label={t("timeline.deleteClip")}
                onClick={handleDeleteSelected}
                icon={<Trash2 className="h-4 w-4" />}
                disabled={selected.length === 0}
              />
            ) : null}
            <PlayerIconButton
              label={t("player.shortcutsAria")}
              onClick={() => setShortcutsOpen(true)}
              icon={<CircleHelp className="h-4 w-4" />}
            />
          </div>
        </div>
      </div>
    </TooltipPrimitive.Provider>
  )

  return (
    <PlayerPipProvider resolve={pipResolve} timeStore={timeStore} playing={playing} getRate={() => controls.getRate()}>
      <div
        ref={rootRef}
        className={cn(
          "flex w-full flex-col",
          fitFrame ? "h-full min-h-0" : "gap-2",
          fillHeight && "h-full min-h-0",
          className,
        )}
      >
        <div
          ref={shellRef}
          className={cn(
            "group/video relative w-full overflow-hidden bg-black shadow-inner",
            fitFrame ? "h-full min-h-0 rounded-none" : "max-h-[min(60vh,520px)] shrink-0 rounded-lg",
            isFullscreen && "flex h-full max-h-none items-center justify-center rounded-none",
            // m-0/border-0/p-0 гасят UA-стили popover (шелл поднят в top layer, см. useShellFullscreen).
            isPseudoFullscreen && "fixed inset-0 z-[120] m-0 border-0 p-0",
          )}
          style={isFullscreen || fitFrame ? undefined : { aspectRatio: String(shellAspectRatio) }}
        >
          <div
            ref={playback.viewportRef}
            className="absolute inset-0 cursor-pointer"
            onClick={handleViewportClick}
            onDoubleClick={() => void toggleFullscreen()}
          />
          {buffering ? (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-white/75 drop-shadow" />
            </div>
          ) : null}
          {shellOverlay ? <div className="pointer-events-none absolute inset-0 z-[25]">{shellOverlay}</div> : null}
          <PlayerPipOverlay />
          {interactiveShellOverlay ? (
            <div className="absolute inset-0 z-[24] overflow-hidden">{interactiveShellOverlay}</div>
          ) : null}
          {interactiveShellOverlay ? null : <StartPlayOverlay playing={playing} timeStore={timeStore} />}
          {shellFloatingAction ? (
            <div className="pointer-events-none absolute inset-0 z-[35]">
              <div className="pointer-events-auto absolute top-3 right-3">{shellFloatingAction}</div>
            </div>
          ) : null}
          <VideoShellOverlay
            shellRef={shellRef}
            isFullscreen={isFullscreen}
            controlsVisible={controlsVisible}
            onControlsPointerEnter={onControlsPointerEnter}
            onControlsPointerLeave={onControlsPointerLeave}
            playing={playing}
            onTogglePlay={togglePlay}
            muted={muted}
            volume={volume}
            setMuted={setMuted}
            setVolume={setVolume}
            toggleFullscreen={toggleFullscreen}
            timeStore={timeStore}
            durationMs={rulerDurationMs}
            fps={fps}
            onSeek={handleSeek}
            onScrubStart={handleOverlayScrubStart}
            onScrub={handleRulerSeek}
            onScrubEnd={handleOverlayScrubEnd}
          />
        </div>

        {showTimeline || transportInline ? (
          <div className={cn("flex w-full min-w-0 flex-col gap-2", fillHeight && "mt-auto shrink-0")}>
            {transportInline ? transport : null}
            {showTimeline ? (
              <TimelineLanes
                doc={doc}
                timelineSources={timelineSources}
                durationMs={rulerDurationMs}
                showTrackHeaders={showTrackHeaders}
                resetKey={sourcesKey}
                timeStore={timeStore}
                playingRef={playingRef}
                selectedIdsByTrack={selectedIdsByTrack}
                onSeek={handleRulerSeek}
                onScrubStart={pauseAll}
                onScrubEnd={handleRulerScrubEnd}
                onSelectClip={handleSelectClip}
                onMoveClip={canMove ? handleMoveClip : () => {}}
                onResizeClip={canResize ? handleResizeClip : () => {}}
                onDragEnd={handleClipDragEnd}
                onClearSelection={() => setSelected([])}
                trackLabel={(track) => (track.kind === "video" ? t("timeline.videoTrackLabel") : track.kind)}
                fillHeight={fillHeight}
                metricsRef={lanesMetricsRef}
              />
            ) : null}
          </div>
        ) : null}
        {transportHost ? createPortal(transport, transportHost) : null}

        <Dialog.Root open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[120] bg-black/50" />
            <Dialog.Content className="ui-dialog-content fixed top-1/2 left-1/2 z-[121] w-[min(92vw,420px)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-ui-border bg-ui-bg-elevated p-4 shadow-2xl focus:outline-none">
              <div className="mb-3 flex items-center justify-between gap-2">
                <Dialog.Title className="text-sm font-semibold text-ui-text-primary">
                  {t("player.shortcutsTitle")}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <Button type="button" size="sm" variant="ghost" aria-label={t("common:close")}>
                    <X className="h-4 w-4" />
                  </Button>
                </Dialog.Close>
              </div>
              <div className="space-y-3">
                {SHORTCUT_SECTIONS.filter((section) =>
                  section.titleKey === "player.shortcutsEdit" ? showEditToolbar : true,
                ).map((section) => (
                  <div key={section.titleKey}>
                    <p className="mb-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
                      {t(section.titleKey)}
                    </p>
                    <ul className="space-y-1">
                      {section.rows.map((row) => (
                        <li key={row.labelKey} className="flex items-center justify-between gap-3 text-xs">
                          <span className="text-ui-text-secondary">{t(row.labelKey)}</span>
                          <span className="flex flex-wrap items-center justify-end gap-1">
                            {row.keys.map((combo, i) => (
                              <span key={i} className="flex items-center gap-0.5">
                                {i > 0 ? <span className="text-ui-text-muted">/</span> : null}
                                {combo.map((key) => (
                                  <Kbd key={key}>{key}</Kbd>
                                ))}
                              </span>
                            ))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </PlayerPipProvider>
  )
}
